import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send, Video } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getVisitorId } from "@/lib/visitorId";
import { supabase } from "@/integrations/supabase/client";
import { VideoCallBooking } from "./VideoCallBooking";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  validatePhoneNumber,
  formatPhoneInput,
  phoneValidationMessages,
} from "@/lib/phoneValidation";
import { cn } from "@/lib/utils";


const translations = {
  de: {
    badge: "Kontakt",
    title: "Starten Sie jetzt mit EAP®",
    subtitle: "Kostenlose Beratung oder direkt registrieren – Sie entscheiden.",
    registerButton: "Kostenlos registrieren",
    contactInfoTitle: "Kontaktinformationen",
    videocall: "Videocall",
    videocallDesc: "Persönliche Beratung per Video",
    email: "E-Mail",
    phone: "Telefon",
    address: "Adresse",
    quickResponse: "💬 Schnelle Antwort garantiert – Wir melden uns innerhalb von 24 Stunden!",
    nameLabel: "Name *",
    namePlaceholder: "Ihr Name",
    emailLabel: "E-Mail *",
    emailPlaceholder: "ihre@email.de",
    phoneLabel: "Telefon",
    phonePlaceholder: "+43 123 456 789",
    messageLabel: "Nachricht *",
    messagePlaceholder: "Ihre Nachricht an uns...",
    sendButton: "Nachricht senden",
    sending: "Wird gesendet...",
    successTitle: "Nachricht gesendet!",
    successDesc: "Vielen Dank für Ihre Nachricht. Wir melden uns innerhalb von 24 Stunden bei Ihnen.",
    errorTitle: "Fehler beim Senden",
    errorDesc: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per E-Mail.",
  },
  en: {
    badge: "Contact",
    title: "Start now with EAP®",
    subtitle: "Free consultation or register directly – you decide.",
    registerButton: "Register for free",
    contactInfoTitle: "Contact Information",
    videocall: "Video call",
    videocallDesc: "Personal consultation via video",
    email: "Email",
    phone: "Phone",
    address: "Address",
    quickResponse: "💬 Quick response guaranteed – We will get back to you within 24 hours!",
    nameLabel: "Name *",
    namePlaceholder: "Your name",
    emailLabel: "Email *",
    emailPlaceholder: "your@email.com",
    phoneLabel: "Phone",
    phonePlaceholder: "+43 123 456 789",
    messageLabel: "Message *",
    messagePlaceholder: "Your message to us...",
    sendButton: "Send message",
    sending: "Sending...",
    successTitle: "Message sent!",
    successDesc: "Thank you for your message. We will get back to you within 24 hours.",
    errorTitle: "Error sending",
    errorDesc: "Please try again later or contact us directly via email.",
  },
  fr: {
    badge: "Contact",
    title: "Commencez maintenant avec EAP®",
    subtitle: "Consultation gratuite ou inscription directe – vous décidez.",
    registerButton: "S'inscrire gratuitement",
    contactInfoTitle: "Informations de contact",
    videocall: "Appel vidéo",
    videocallDesc: "Consultation personnelle par vidéo",
    email: "E-mail",
    phone: "Téléphone",
    address: "Adresse",
    quickResponse: "💬 Réponse rapide garantie – Nous vous recontactons dans les 24 heures!",
    nameLabel: "Nom *",
    namePlaceholder: "Votre nom",
    emailLabel: "E-mail *",
    emailPlaceholder: "votre@email.com",
    phoneLabel: "Téléphone",
    phonePlaceholder: "+43 123 456 789",
    messageLabel: "Message *",
    messagePlaceholder: "Votre message...",
    sendButton: "Envoyer le message",
    sending: "Envoi en cours...",
    successTitle: "Message envoyé!",
    successDesc: "Merci pour votre message. Nous vous recontacterons dans les 24 heures.",
    errorTitle: "Erreur lors de l'envoi",
    errorDesc: "Veuillez réessayer plus tard ou nous contacter directement par e-mail.",
  },
  nl: {
    badge: "Contact",
    title: "Begin nu met EAP®",
    subtitle: "Gratis advies of direct registreren – u beslist.",
    registerButton: "Gratis registreren",
    contactInfoTitle: "Contactinformatie",
    videocall: "Videogesprek",
    videocallDesc: "Persoonlijk advies via video",
    email: "E-mail",
    phone: "Telefoon",
    address: "Adres",
    quickResponse: "💬 Snelle reactie gegarandeerd – We nemen binnen 24 uur contact met u op!",
    nameLabel: "Naam *",
    namePlaceholder: "Uw naam",
    emailLabel: "E-mail *",
    emailPlaceholder: "uw@email.com",
    phoneLabel: "Telefoon",
    phonePlaceholder: "+43 123 456 789",
    messageLabel: "Bericht *",
    messagePlaceholder: "Uw bericht aan ons...",
    sendButton: "Bericht verzenden",
    sending: "Verzenden...",
    successTitle: "Bericht verzonden!",
    successDesc: "Bedankt voor uw bericht. We nemen binnen 24 uur contact met u op.",
    errorTitle: "Fout bij verzenden",
    errorDesc: "Probeer het later opnieuw of neem direct contact met ons op via e-mail.",
  },
  it: {
    badge: "Contatto",
    title: "Inizia ora con EAP®",
    subtitle: "Consulenza gratuita o registrazione diretta – decidi tu.",
    registerButton: "Registrati gratuitamente",
    contactInfoTitle: "Informazioni di contatto",
    videocall: "Videochiamata",
    videocallDesc: "Consulenza personale tramite video",
    email: "E-mail",
    phone: "Telefono",
    address: "Indirizzo",
    quickResponse: "💬 Risposta rapida garantita – Ti ricontattiamo entro 24 ore!",
    nameLabel: "Nome *",
    namePlaceholder: "Il tuo nome",
    emailLabel: "E-mail *",
    emailPlaceholder: "tua@email.com",
    phoneLabel: "Telefono",
    phonePlaceholder: "+43 123 456 789",
    messageLabel: "Messaggio *",
    messagePlaceholder: "Il tuo messaggio...",
    sendButton: "Invia messaggio",
    sending: "Invio in corso...",
    successTitle: "Messaggio inviato!",
    successDesc: "Grazie per il tuo messaggio. Ti ricontatteremo entro 24 ore.",
    errorTitle: "Errore durante l'invio",
    errorDesc: "Riprova più tardi o contattaci direttamente via e-mail.",
  },
  es: {
    badge: "Contacto",
    title: "Comience ahora con EAP®",
    subtitle: "Consulta gratuita o regístrese directamente – usted decide.",
    registerButton: "Regístrese gratis",
    contactInfoTitle: "Información de contacto",
    videocall: "Videollamada",
    videocallDesc: "Consulta personal por video",
    email: "Correo electrónico",
    phone: "Teléfono",
    address: "Dirección",
    quickResponse: "💬 Respuesta rápida garantizada – ¡Nos pondremos en contacto con usted en 24 horas!",
    nameLabel: "Nombre *",
    namePlaceholder: "Su nombre",
    emailLabel: "Correo electrónico *",
    emailPlaceholder: "su@email.com",
    phoneLabel: "Teléfono",
    phonePlaceholder: "+43 123 456 789",
    messageLabel: "Mensaje *",
    messagePlaceholder: "Su mensaje para nosotros...",
    sendButton: "Enviar mensaje",
    sending: "Enviando...",
    successTitle: "¡Mensaje enviado!",
    successDesc: "Gracias por su mensaje. Nos pondremos en contacto con usted en 24 horas.",
    errorTitle: "Error al enviar",
    errorDesc: "Por favor, inténtelo de nuevo más tarde o contáctenos directamente por correo electrónico.",
  },
};

export const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const phoneMessages = phoneValidationMessages[language];
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState<string>("");
  const formStartedAt = useRef(Date.now());
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "",
  });


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.phone.trim()) {
      const phoneCheck = validatePhoneNumber(formData.phone);
      if (!phoneCheck.valid) {
        const message = phoneCheck.error
          ? phoneMessages[phoneCheck.error]
          : phoneMessages.invalid;
        setPhoneError(message);
        toast({
          title: t.errorTitle,
          description: message,
          variant: "destructive",
        });
        return;
      }
    }

    setIsSubmitting(true);

    try {

      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: { ...formData, visitorId: getVisitorId(), formStartedAt: formStartedAt.current },
      });

      if (error) throw error;

      toast({
        title: t.successTitle,
        description: t.successDesc,
      });

      setFormData({ name: "", email: "", phone: "", message: "", website: "" });
      formStartedAt.current = Date.now();
    } catch (error: any) {
      console.error('Error sending contact email:', error);
      toast({
        title: t.errorTitle,
        description: t.errorDesc,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-12 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 animate-fade-up">
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            {t.badge}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
            {t.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
              onClick={() => window.location.href = 'https://shop.eap-abutments.com/login/signup'}
            >
              {t.registerButton}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6 animate-fade-in">
            <div>
              <h3 className="text-2xl font-semibold text-foreground mb-5">
                {t.contactInfoTitle}
              </h3>
              
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Video className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">{t.videocall}</div>
                    <p className="text-muted-foreground mb-2 text-sm">
                      {t.videocallDesc}
                    </p>
                    <VideoCallBooking />
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">{t.email}</div>
                    <a 
                      href="mailto:info@eap-abutments.com" 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      info@eap-abutments.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">{t.phone}</div>
                    <a 
                      href="tel:+436776205654" 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      +43 (0)677 620 565 43
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground mb-1">{t.address}</div>
                    <p className="text-muted-foreground">
                      Lorettostraße 26<br />
                      6060 Hall in Tirol<br />
                      Österreich
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-fade-in">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-4">
              <p className="text-sm text-foreground font-medium">{t.quickResponse}</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5 bg-card p-6 rounded-2xl border border-border shadow-card">
              <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
                <Label htmlFor="contact-website">Website</Label>
                <Input
                  id="contact-website"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="name">{t.nameLabel}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2"
                  placeholder={t.namePlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="email">{t.emailLabel}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-2"
                  placeholder={t.emailPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="phone">{t.phoneLabel}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const cleaned = raw.replace(/[^+\d\s()-]/g, "");
                    setFormData({ ...formData, phone: cleaned });
                    if (phoneError) setPhoneError("");
                  }}
                  onBlur={(e) => {
                    const formatted = formatPhoneInput(e.target.value);
                    setFormData((prev) => ({ ...prev, phone: formatted }));
                    if (formatted) {
                      const check = validatePhoneNumber(formatted);
                      if (!check.valid) {
                        setPhoneError(
                          check.error ? phoneMessages[check.error] : phoneMessages.invalid
                        );
                      } else {
                        setPhoneError("");
                      }
                    } else {
                      setPhoneError("");
                    }
                  }}
                  className={cn("mt-2", phoneError && "border-destructive focus-visible:ring-destructive")}
                  placeholder={t.phonePlaceholder}
                  aria-invalid={!!phoneError}
                  aria-describedby={phoneError ? "contact-phone-error" : undefined}
                />
                {phoneError && (
                  <p id="contact-phone-error" className="text-sm text-destructive mt-1">
                    {phoneError}
                  </p>
                )}
              </div>


              <div>
                <Label htmlFor="message">{t.messageLabel}</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="mt-2 min-h-[100px]"
                  placeholder={t.messagePlaceholder}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
                size="lg"
                disabled={isSubmitting}
              >
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting ? t.sending : t.sendButton}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};